import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from "@angular/core";
import {ActivatedRoute, ParamMap} from "@angular/router";
import {BasePage} from "~/app/pages/base.page";
import {NetworkConnectionType} from "~/app/core/network.service";
import {InteractionType} from "~/app/core/model/interaction-type";
import {InteractionComponent} from "~/app/core/model/interaction-component";
import {validEmail} from "~/app/core/util/string-util";
import {CoreService} from "~/app/core/core.service";

@Component({
    selector: "Forgot Password",
    templateUrl: "./forgot-password.page.html",
    styleUrls: ["./forgot-password.page.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ForgotPasswordPage extends BasePage implements OnInit, AfterViewInit {
    offerId: number = null;
    email: string = "";

    showResetPasswordRequest: boolean = true;
    showResetPassword: boolean = false;
    showNewPassword: boolean = false;

    resetEmailText: string = "";
    resetCodeText: string = "";
    resetPasswordText: string = "";

    tab = "request";

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly activatedRoute: ActivatedRoute,
        private readonly core: CoreService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.activatedRoute.queryParamMap.subscribe((value: ParamMap) => {
            if (value.has("offerId")) {
                this.offerId = Number(value.get("offerId"));
            }
            if (value.has("allowBack")) {
                this.allowBack = value.get("allowBack") === "true";
            }
        });
    }

    ngAfterViewInit(): void {
    }

    // newPasswordFieldLoaded(args) {
    //     const textField: TextField = args.object;
    //     if (application.android) {
    //         this.passwordTypeFace = textField.android.getTypeface();
    //     }
    //     this.updatePasswordVisibility();
    // }
    //
    goBack() {
        if (!this.waiting) {
            this.waiting = true;
            this.waitingSpinning = false;
            if (this.allowBack) {
                this.core.nav.navBack();
            } else {
                this.core.nav.navPageSearching(this.core.api, this.offerId);
            }
        }
    }

    onToggleResetPasswordSecure() {
        this.showNewPassword = !this.showNewPassword;
        this.cd.detectChanges();
    }

    onRequestReset() {
        this.email = this.resetEmailText;

        if (this.email.length < 3 || !validEmail(this.email)) {
            this.core.toast.toastError({
                header: "Invalid Email",
                message: "Please enter a valid email address."
            });
        } else {
            if (this.core.net.connectionType === NetworkConnectionType.None) {
                this.handleNoData("request");
            } else {
                this.waiting = true;
                this.cd.detectChanges();
                this.core.api.user_forgotPassword(this.email).then((result) => {
                    this.waiting = false;
                    if (result.success) {
                        this.showResetPassword = true;
                        this.tab = "forgot";
                    } else {
                        this.core.toast.toastError({
                            header: "Problem Requesting Reset",
                            message: "There was a problem requesting your password reset. Please verify that your device data is enabled and try again."
                        });
                    }
                    this.cd.detectChanges();
                }, (reason) => {
                    this.waiting = false;
                    this.core.toast.toastError({
                        header: "Problem Requesting Reset",
                        message: "There was a problem requesting your password reset. Please verify that your device data is enabled and try again."
                    });
                    this.cd.detectChanges();
                });
            }
        }
    }

    onResetPassword() {
        if (this.resetCodeText.length > 0) {
            const resetCode = this.resetCodeText;
            const newPassword = this.resetPasswordText;

            if (newPassword.length < 8) {
                this.core.toast.toastError({
                    header: "Cannot Reset password",
                    message: "Please enter a password at least 8 characters long."
                });
            } else {
                if (this.core.net.connectionType === NetworkConnectionType.None) {
                    this.handleNoData("reset");
                } else {
                    this.waiting = true;
                    this.cd.detectChanges();
                    this.core.api.user_resetPassword(this.email, resetCode, newPassword).then((result) => {
                        if (result.success) {
                            this.waitingSpinning = false;
                            this.core.api.interaction(InteractionType.Login, InteractionComponent.ForgotPasswordPage);
                            this.core.api.interaction(InteractionType.ResetPassword, InteractionComponent.ForgotPasswordPage);
                            this.core.toast.toastSuccess({
                                header: "Password Reset Success",
                                message: "You have successfully reset your password. Now enjoy your FREE offers!",
                                onHide: () => {
                                    this.core.nav.navPageSearching(this.core.api, this.offerId);
                                }
                            });
                        } else {
                            this.waiting = false;
                            this.core.toast.toastError({
                                header: "Problem Resetting Password",
                                message: "There was a problem resetting your password. Please verify that everything is correct and try again."
                            });
                        }
                        this.cd.detectChanges();
                    }, (reason) => {
                        this.waiting = false;
                        if (reason === "InvalidCode") {
                            this.core.toast.toastError({
                                header: "Invalid Code",
                                message: "The reset code was invalid. Try again, or use 'Re-send code?' to request a new code."
                            });
                        } else {
                            this.core.toast.toastError({
                                header: "Trouble",
                                message: "There was a problem requesting your password reset. Check all your info, make sure you have network connectivity, and try again."
                            });
                        }
                        this.cd.detectChanges();
                    });
                }
            }
        }
    }

    onResendResetCode() {
        this.core.dialogs.confirm({
            title: "Re-send Reset Code",
            message: "Would you like us to send another password reset code to " + this.email + "?",
            okButtonText: "Send",
            cancelButtonText: "Cancel"
        }).then((result) => {
            if (result === true) {
                if (this.core.net.connectionType === NetworkConnectionType.None) {
                    this.handleNoData("request");
                } else {
                    this.waiting = true;
                    this.cd.detectChanges();
                    this.core.api.user_forgotPassword(this.email).then((successresult) => {
                        this.waiting = false;
                        if (!successresult.success) {
                            this.core.toast.toastError({
                                header: "Problem Re-sending Code",
                                message: "There was a problem re-sending your password reset code."
                            });
                        }
                        this.cd.detectChanges();
                    }, (error) => {
                        this.waiting = false;
                        this.core.toast.toastError({
                            header: "Problem Re-sending Code",
                            message: "There was a problem re-sending your password reset code."
                        });
                        this.cd.detectChanges();
                    });
                }
            }
        });
    }

    private handleNoData(type: "request" | "reset") {
        if (type === "request") {
            this.core.toast.toastError({
                header: "No Data",
                message: "Sorry! We can't send you a password reset code without data or wi-fi available.\n\nPlease enable data and try again."
            });
        } else if (type === "reset") {
            this.core.toast.toastError({
                header: "No Data",
                message: "Sorry! We can't reset your password without data or wi-fi available.\n\nPlease enable data and try again."
            });
        }
    }
}
